import React, { useMemo, useState } from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import {
  ATTRIBUTION_STATUS_COLORS,
  ATTRIBUTION_STATUS_OPTIONS,
  EMPTY_STATES,
  getStatusColor,
} from '../constants';
import { formatCurrency, formatDate } from '../utils/formatters';
import { FilterGroup, FilterLabel, FilterRow, InlineNotice } from '../Styles';
import CreatorProgramTable from '../components/CreatorProgramTable';
import FilterSelect from '../components/FilterSelect';
import ListPagination from '../components/ListPagination';
import QueryErrorState from '../components/QueryErrorState';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { usePaginationState } from '../hooks/usePaginationState';
import { useCreatorOptions, useStoreOptions } from '../hooks/useSelectOptions';
import { useAttributions } from '../hooks/useCreatorQueries';
import type { Attribution, AttributionStatus } from '../types';
import type { DateRange } from '../../../commonComponents/DateRangePicker/types';

const { Text } = Typography;

/**
 * §1.6 — attribution ledger.
 *
 * An auditable, order-level list of every commission-earning sale and refund
 * adjustment across the whole program.
 *
 * Two things the docs are explicit about, both reflected here:
 * - Filters are separate query params combined with **AND** server-side
 *   (`creator=X&status=payable` means "X's payable rows", not "either"), so every
 *   filter goes to the API rather than narrowing rows in the browser.
 * - Status moves `pending → payable → paid` (or `reversed`) automatically over time.
 *   There is deliberately **no status action** on this screen — status only changes
 *   indirectly, through payouts.
 */
const AttributionsTab: React.FC = () => {
  const { dateRange, setDateRange, apiRange } = useDateRangeFilter();
  const { page, pageSize, onPaginationChange, withPageReset } = usePaginationState();

  const [creatorId, setCreatorId] = useState<string | undefined>();
  const [storeId, setStoreId] = useState<string | undefined>();
  const [status, setStatus] = useState<AttributionStatus | undefined>();

  const { options: storeOptions } = useStoreOptions();
  const { options: creatorOptions, loading: creatorsLoading } = useCreatorOptions();

  const params = useMemo(
    () => ({
      creator: creatorId,
      store: storeId,
      status,
      ...apiRange,
      page,
      limit: pageSize,
    }),
    [creatorId, storeId, status, apiRange, page, pageSize]
  );

  const { data, isLoading, isFetching, isError, refetch } = useAttributions(params);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  /** Any filter change invalidates the current page offset. */
  const handleRangeChange = useMemo(
    () => withPageReset<DateRange>(setDateRange),
    [withPageReset, setDateRange]
  );
  const handleCreatorChange = useMemo(
    () => withPageReset<string | undefined>(setCreatorId),
    [withPageReset]
  );
  const handleStoreChange = useMemo(
    () => withPageReset<string | undefined>(setStoreId),
    [withPageReset]
  );
  const handleStatusChange = useMemo(
    () => withPageReset<AttributionStatus | undefined>(setStatus),
    [withPageReset]
  );

  const columns: ColumnsType<Attribution> = useMemo(
    () => [
      {
        title: 'ORDER',
        key: 'orderId',
        fixed: 'left',
        width: 170,
        render: (_, record) => (
          <Tooltip title={record.orderId}>
            <Text ellipsis style={{ maxWidth: 150 }}>
              {record.orderId}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: 'TYPE',
        key: 'entryType',
        width: 150,
        render: (_, record) =>
          // A refund_adjustment is a negative-earnings correction linked back to its
          // original sale — the ledger is already netted per row, so this is a marker,
          // not something to reconcile separately.
          record.entryType === 'refund_adjustment' ? (
            <Tooltip title="Negative correction linked to an original sale. The ledger is already netted per row.">
              <Tag color="volcano">Refund adj.</Tag>
            </Tooltip>
          ) : (
            <Tag color="default">Sale</Tag>
          ),
      },
      {
        title: 'CREATOR',
        key: 'creator',
        width: 170,
        render: (_, record) => record.creator?.name || '—',
      },
      {
        title: 'STORE',
        key: 'store',
        width: 150,
        render: (_, record) => record.store?.name || '—',
      },
      {
        title: 'CHANNEL',
        key: 'channel',
        width: 130,
        render: (_, record) =>
          record.channel === 'code' ? (
            <Tag color="geekblue">{record.code?.code || 'Code'}</Tag>
          ) : (
            <Tag color="purple">Link</Tag>
          ),
      },
      {
        title: 'SUBTOTAL',
        key: 'subtotal',
        width: 120,
        render: (_, record) => formatCurrency(record.subtotal),
      },
      {
        title: 'COMMISSION BASE',
        key: 'commissionBase',
        width: 155,
        render: (_, record) => formatCurrency(record.commissionBase),
      },
      {
        title: 'CUSTOMER DISCOUNT',
        key: 'customerDiscount',
        width: 170,
        render: (_, record) => formatCurrency(record.customerDiscount),
      },
      {
        // Row-level figures, unlike the aggregate reports: an attribution row has no
        // separate "net" variant because a refund is its own negative row here.
        title: 'CREATOR EARNINGS',
        key: 'creatorEarnings',
        width: 165,
        render: (_, record) => (
          <strong style={{ color: record.creatorEarnings < 0 ? '#cf1322' : undefined }}>
            {formatCurrency(record.creatorEarnings)}
          </strong>
        ),
      },
      {
        title: 'STATUS',
        key: 'status',
        width: 120,
        render: (_, record) => (
          <Tag color={getStatusColor(record.status, ATTRIBUTION_STATUS_COLORS)}>
            {record.status}
          </Tag>
        ),
      },
      {
        title: 'DATE',
        key: 'createdAt',
        width: 130,
        render: (_, record) => formatDate(record.createdAt),
      },
    ],
    []
  );

  if (isError && rows.length === 0) {
    return (
      <QueryErrorState
        title="Could not load the attribution ledger"
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <>
      <FilterRow style={{ marginBottom: 16 }}>
        <FilterGroup>
          <FilterLabel>Date range</FilterLabel>
          <DateRangePicker
            selectedDateRange={dateRange}
            onDateRangeChange={handleRangeChange}
          />
        </FilterGroup>

        <FilterSelect
          label="Creator"
          placeholder="All creators"
          options={creatorOptions}
          loading={creatorsLoading}
          value={creatorId}
          onChange={handleCreatorChange}
          minWidth={220}
        />

        <FilterSelect
          label="Store"
          placeholder="All stores"
          options={storeOptions}
          value={storeId}
          onChange={handleStoreChange}
          minWidth={180}
        />

        <FilterSelect<AttributionStatus>
          label="Status"
          placeholder="All statuses"
          options={ATTRIBUTION_STATUS_OPTIONS}
          value={status}
          onChange={handleStatusChange}
          searchable={false}
          minWidth={160}
        />
      </FilterRow>

      <InlineNotice>
        Status advances automatically (pending → payable → paid, or reversed on a
        cancellation or refund). It is not editable here — it changes through payouts.
      </InlineNotice>

      <CreatorProgramTable<Attribution>
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={isLoading || isFetching}
        emptyText={EMPTY_STATES.ATTRIBUTIONS}
      />

      <ListPagination
        visible={rows.length > 0}
        totalCount={total}
        currentPage={page}
        pageSize={pageSize}
        onChange={onPaginationChange}
        totalLabel="Total rows"
      />
    </>
  );
};

export default AttributionsTab;
